import { Subscription } from 'rxjs';

import {
    IGroupTotalsByEntityReport,
    IGroupTotalsByEntityRow,
    IKeyValue,
    ReportColumnNames,
    ReportExtensions
} from '@spa/api';

import { ReportTableColumn, ReportTableConfigInterface, ReportTableGroupedColumn } from '../../../shared/components/report-table';

import { EventsService } from '../services';

import { TradingReportWithTotalRows } from './trading-report-with-total-rows';

import { TotalsByEntitiesReportTableConfig } from 'appAssets/descriptors';
import { Helper } from '../pages/panel-clients/pages/panel-clients-edit/panel-clients-edit.imports';

export class TotalsByEntityReport extends TradingReportWithTotalRows<IGroupTotalsByEntityReport> {
    private readonly staticTableColumnSelectors: string[];
    protected exportTriggerSubscription: Subscription;
    public reportData: IGroupTotalsByEntityReport;

    constructor(public eventsService: EventsService) {
        super(eventsService);

        this.staticTableColumnSelectors = [
            ...this.feesSelectors,
            ReportColumnNames.Account,
            ReportColumnNames.Currency,
            ReportColumnNames.AssetType,
            ReportColumnNames.Orders,
            ReportColumnNames.Fills,
            ReportColumnNames.Qty,
            ReportColumnNames.Gross,
            ReportColumnNames.Net,
            ReportColumnNames.Transfers,
            ReportColumnNames.Cash,
            ReportColumnNames.Unrealized,
            ReportColumnNames.PreviousUnrealized,
            ReportColumnNames.PreviousCash,
            ReportColumnNames.AdjNet
        ] as string[];
    }

    onInit(): void {
        super.onInit();
    }

    onDestroy(): void {
        super.onDestroy();

        if (this.exportTriggerSubscription) {
            this.exportTriggerSubscription.unsubscribe();
        }
    }

    getTotalRows(): IKeyValue<string, IGroupTotalsByEntityRow>[] {
        return this.getReportCollection().totals;
    }

    getReportCollection(): IGroupTotalsByEntityReport {
        return this.reportData;
    }

    getReportData(): IGroupTotalsByEntityRow[] {
        return this.reportData.rows;
    }

    getReportTableConfig(): ReportTableConfigInterface {
        const tableConfig = TotalsByEntitiesReportTableConfig();

        if (this.reportData.rows.length > 0) {
            const total = ReportExtensions.getValueByKey(this.reportData.totals, 'Total');
            let adjustments;
            if(Helper.isDefined(total) && Helper.isDefined(total.adjustmentFees)){
                  adjustments = ReportExtensions.getAdjustmentList(total);
            }else{
                  adjustments = [];
            }

            this.applyAdjustmentsConfig(adjustments, tableConfig);
        }

        tableConfig.columns.sort((
            current: ReportTableColumn | ReportTableGroupedColumn,
            next: ReportTableColumn | ReportTableGroupedColumn
        ) => current.sortIndex - next.sortIndex);

        return tableConfig;
    }

    tableDataItemWithIcon(colId: string): boolean {
        return colId === ReportColumnNames.Gross;
    }

    mapDataTableItem(
        colId: string,
        reportItem: IGroupTotalsByEntityRow,
        isTotalRow = false,
        totalHeader: string = null): string | number {
        if (this.staticTableColumnSelectors.indexOf(colId) < 0) {
            let value = 0;

            if (reportItem.adjustmentFees) {
                value = reportItem.adjustmentFees[colId];
            }

            return ReportExtensions.toDecimalString(value);
        }

        switch (colId) {
            case ReportColumnNames.Account:
                return isTotalRow
                    ? totalHeader
                    : reportItem.accountGroupReferenceId || reportItem.accountReferenceId;
            case ReportColumnNames.AssetType:
                return !isTotalRow ? reportItem.assetType : '';
            case ReportColumnNames.Orders:
                return ReportExtensions.toIntegerString(reportItem.orders);
            case ReportColumnNames.Fills:
                return ReportExtensions.toIntegerString(reportItem.fills);
            case ReportColumnNames.Qty:
                return ReportExtensions.toDecimalString(reportItem.qty, 3, 3, true, true);
            case ReportColumnNames.Unrealized:
                return ReportExtensions.toDecimalString(reportItem.unrealized);
            case ReportColumnNames.Cash:
                return ReportExtensions.toDecimalString(reportItem.cash);
            case ReportColumnNames.PreviousCash:
                return ReportExtensions.toDecimalString(reportItem.previousCash);
            case ReportColumnNames.PreviousUnrealized:
                return ReportExtensions.toDecimalString(reportItem.previousUnrealized);
            case ReportColumnNames.Gross:
                return ReportExtensions.toDecimalString(reportItem.gross);
            case ReportColumnNames.Net:
                return ReportExtensions.toDecimalString(reportItem.net);
            case ReportColumnNames.AdjNet:
                return ReportExtensions.toDecimalString(reportItem.adjNet);
            case ReportColumnNames.Transfers:
                return ReportExtensions.toDecimalString(reportItem.transfers);
            case ReportColumnNames.Currency:
                return reportItem.currency?.toUpperCase();
            default:
                return '';
        }
    }

    isShiftedTableData(colId: ReportColumnNames): boolean {
        return [ReportColumnNames.Account, ReportColumnNames.AssetType, ReportColumnNames.Currency].indexOf(colId) < 0;
    }

    validateInitialData(data: any): void {
    }
}
